import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, PhoneOff, Video } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Global Incoming Call popup.
 *
 * Polls notifications every 6s. When an `incoming_call` notification arrives
 * (severity=critical, unread) that we haven't already dismissed locally, shows
 * a fixed bottom-right popup with a soft ringing tone and two CTAs:
 *   - Join Call  → marks notification read + navigates to /teleconsult/room/{cid}
 *   - Reject     → calls POST /teleconsult/consultations/{cid}/invitee-status (status=declined)
 *                  + marks notification read
 *
 * Ringing is generated via Web Audio API so no audio asset is needed.
 * Calls older than 90s are auto-ignored to prevent showing stale rings on a refresh.
 */
const STALE_AFTER_MS = 90_000;
const POLL_MS = 6_000;
const dismissed = new Set(); // session-scoped — survives navigations, not refreshes

export default function IncomingCallProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(null); // current notification being rung
  const ringRef = useRef(null);
  const titleRef = useRef(typeof document !== "undefined" ? document.title : "");

  // ---- Ring tone (Web Audio API) ----
  const stopRing = useCallback(() => {
    if (ringRef.current) {
      try { ringRef.current.stop(); } catch {/*ignore*/}
      ringRef.current = null;
    }
    if (typeof document !== "undefined") document.title = titleRef.current;
  }, []);

  const startRing = useCallback(() => {
    if (ringRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const gain = ctx.createGain();
      gain.gain.value = 0.001; // start silent — ramp up
      gain.connect(ctx.destination);
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      osc1.type = "sine"; osc1.frequency.value = 440;
      osc2.type = "sine"; osc2.frequency.value = 480;
      osc1.connect(gain); osc2.connect(gain);
      // Ringing pattern: 1s on / 2s off (loops)
      const now = ctx.currentTime;
      for (let i = 0; i < 20; i++) {
        const t = now + i * 3;
        gain.gain.setValueAtTime(0.001, t);
        gain.gain.exponentialRampToValueAtTime(0.05, t + 0.05);
        gain.gain.setValueAtTime(0.05, t + 1.0);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 1.05);
      }
      osc1.start(now); osc2.start(now);
      osc1.stop(now + 60); osc2.stop(now + 60);
      // Each cleanup step in its own try/catch so one failure can't skip the others
      ringRef.current = {
        stop: () => {
          try { gain.gain.cancelScheduledValues(ctx.currentTime); } catch { /* ignore */ }
          try { gain.gain.setValueAtTime(0, ctx.currentTime); } catch { /* ignore */ }
          try { osc1.stop(); } catch { /* already scheduled to stop */ }
          try { osc2.stop(); } catch { /* already scheduled to stop */ }
          try { osc1.disconnect(); } catch { /* ignore */ }
          try { osc2.disconnect(); } catch { /* ignore */ }
          try { gain.disconnect(); } catch { /* ignore */ }
          try { ctx.close(); } catch { /* ignore */ }
        },
      };
    } catch { /* audio blocked — show popup silently */ }
  }, []);

  // Tab-title flash
  useEffect(() => {
    if (!active) return;
    let toggle = false;
    const original = titleRef.current;
    const t = setInterval(() => {
      toggle = !toggle;
      document.title = toggle ? "📞 Incoming call…" : original;
    }, 1000);
    return () => { clearInterval(t); document.title = original; };
  }, [active]);

  // ---- Notification polling ----
  const fetchNext = useCallback(async () => {
    if (!user) return;
    if (active) return; // already showing one
    try {
      const r = await api.get("/notifications", { params: { limit: 20 } });
      const items = r.data?.items || r.data || [];
      const candidate = items.find((n) => {
        if ((n.event || "") !== "incoming_call") return false;
        if (n.is_read || n.read) return false;
        if (dismissed.has(n.id)) return false;
        if (n.created_at) {
          const age = Date.now() - new Date(n.created_at).getTime();
          if (age > STALE_AFTER_MS) return false;
        }
        return true;
      });
      if (candidate) setActive(candidate);
    } catch { /* silent */ }
  }, [user, active]);

  useEffect(() => {
    if (!user) return undefined;
    fetchNext();
    const id = setInterval(fetchNext, POLL_MS);
    return () => clearInterval(id);
  }, [user, fetchNext]);

  useEffect(() => {
    if (active) startRing();
    else stopRing();
    return () => stopRing();
  }, [active, startRing, stopRing]);

  // ---- Actions ----
  const consultationId = active?.request_id;

  async function join() {
    const a = active;
    setActive(null);
    if (!a) return;
    dismissed.add(a.id);
    try { await api.post(`/notifications/${a.id}/read`); } catch { /* ignore */ }
    if (a.action_url) navigate(a.action_url);
    else if (consultationId) navigate(`/teleconsult/room/${consultationId}`);
  }

  async function reject() {
    const a = active;
    setActive(null);
    if (!a) return;
    dismissed.add(a.id);
    try {
      if (consultationId && user?.email) {
        await api.post(`/teleconsult/consultations/${consultationId}/invitee-status`, {
          invitee_email: user.email,
          status: "declined",
        });
      }
      await api.post(`/notifications/${a.id}/read`);
    } catch { /* ignore */ }
  }

  return (
    <>
      {children}
      {active && (
        <div
          className="fixed bottom-6 right-6 z-[9999] w-[360px] hah-card border-l-4 border-l-[#10B981] shadow-2xl"
          style={{ animation: "incomingCallPulse 1.6s ease-in-out infinite" }}
          data-testid="incoming-call-popup"
        >
          <style>{`
            @keyframes incomingCallPulse {
              0%, 100% { box-shadow: 0 10px 40px rgba(16,185,129,.18), 0 0 0 0 rgba(16,185,129,.4); }
              50%      { box-shadow: 0 10px 50px rgba(16,185,129,.30), 0 0 0 10px rgba(16,185,129,0); }
            }
          `}</style>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-[#0B7E8C] flex items-center justify-center text-white">
                  <Video className="w-6 h-6" />
                </div>
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#10B981] animate-pulse" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#10B981]">
                  Incoming Video Call
                </div>
                <div className="text-sm font-bold text-[#1F2937] truncate">
                  {active.title || "Hospital @ Home"}
                </div>
                {active.body && (
                  <div className="text-[11px] text-[#6B7280] truncate" title={active.body}>
                    {active.body}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={reject}
                className="flex-1 h-10 rounded-lg bg-[#EF4444] hover:bg-[#DC2626] text-white text-sm font-semibold flex items-center justify-center gap-1.5"
                data-testid="incoming-call-reject"
              >
                <PhoneOff className="w-4 h-4" /> Reject
              </button>
              <button
                onClick={join}
                className="flex-1 h-10 rounded-lg bg-[#10B981] hover:bg-[#059669] text-white text-sm font-semibold flex items-center justify-center gap-1.5"
                data-testid="incoming-call-join"
              >
                <Phone className="w-4 h-4" /> Join Call
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
